"use client";

import { useBadgeTextTheme } from "@/shared/lib/hooks/use-badge-text-theme";
import { useTournamentStatusLabel } from "@/shared/lib/hooks/use-tournament-status-label";
import { cn } from "@/shared/lib/utils";
import { isDraftTournament } from "@/entities/tournament/lib/tournament-lifecycle";

import type { Tournament } from "@/entities/tournament/model/types";

const STATUS_GRADIENT: Record<string, string> = {
  Draft: "bg-gradient-to-r from-slate-400 to-slate-500",
  Upcoming: "bg-gradient-to-r from-blue-500 to-blue-600",
  RegistrationOpen: "bg-gradient-to-r from-emerald-500 to-green-600",
  RegistrationClosed: "bg-gradient-to-r from-amber-500 to-yellow-500",
  InProgress: "bg-gradient-to-r from-red-500 to-rose-600",
  Ongoing: "bg-gradient-to-r from-red-500 to-rose-600",
  DraftInProgress: "bg-gradient-to-r from-violet-500 to-purple-600",
  Completed: "bg-gradient-to-r from-slate-500 to-slate-600",
  Cancelled: "bg-gradient-to-r from-red-600 to-red-700",
  AwaitingBracket: "bg-gradient-to-r from-amber-500 to-orange-500",
  Unknown: "bg-gradient-to-r from-slate-400 to-slate-500",
  draft: "bg-gradient-to-r from-blue-500 to-blue-600",
  active: "bg-gradient-to-r from-red-500 to-rose-600",
  completed: "bg-gradient-to-r from-slate-500 to-slate-600",
  cancelled: "bg-gradient-to-r from-red-600 to-red-700",
};

interface TournamentStatusBadgeProps {
  tournament: Tournament;
  className?: string;
  showLiveDot?: boolean;
}

export function TournamentStatusBadge({
  tournament,
  className,
  showLiveDot = true,
}: TournamentStatusBadgeProps) {
  const textTheme = useBadgeTextTheme();
  const statusLabel = useTournamentStatusLabel();
  const statusKey = (tournament.status ?? "Draft") as string;

  /**
   * KAN-359: «Ожидание сетки» — честная формулировка для Classic, но для драфта
   * этот же статус значит другое: пики закончились, команды собраны. Отдельный
   * статус ради переименования заводить не стали (это проход по всем проверкам
   * статуса на бэкенде ради слова), поэтому подпись уточняется здесь — она
   * зависит от типа турнира, а справочник бейджей ключуется одной лишь строкой.
   */
  const label =
    statusKey === "AwaitingBracket" &&
    isDraftTournament(tournament) &&
    tournament.isDraftCompleted
      ? statusLabel("teamsFormed")
      : statusLabel(statusKey);
  const gradient = STATUS_GRADIENT[statusKey] ?? STATUS_GRADIENT["Unknown"];
  // Живая точка — только когда реально идут матчи. Драфт горит своим цветом, но
  // «в эфире» его называть нельзя: играть ещё нечего.
  const isLive =
    tournament.status === "active" ||
    tournament.status === "InProgress" ||
    tournament.status === "Ongoing";
  const textClass = textTheme === "dark" ? "text-white" : "text-black";

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-md border-0 px-2 py-0.5 text-xs font-medium whitespace-nowrap shrink-0",
        gradient,
        textClass,
        className,
      )}
    >
      {showLiveDot && isLive && (
        <span className="relative flex h-2 w-2 mr-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
      )}
      {label}
    </span>
  );
}
